/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Dialog,
  Grid,
  InputAdornment,
  IconButton,
  GridListTile,
  GridList,
} from '@material-ui/core';
import { OpenInNew } from '@material-ui/icons';
import { GlobalTextInputProps } from 'Types';
import {
  LoadingBounce,
  WithDialogsProps,
  withDialogs,
  TextField,
  Panel,
} from '@michelin/acid-components';
import { withTranslation, WithTranslation } from '@michelin/central-provider';
import { ClosableDialogTitle } from 'components/ClosableDialog/ClosableDialogTitle';
import { ClosableDialogContent } from 'components/ClosableDialog/ClosableDialogContent';
import { getGooglePlacesInfo, getGooglePlacesPhotoUrls, GooglePlacesInfo } from '../../Types/GooglePlaces';
import { Map } from '.';

interface GooglePlacesDetailsProps extends WithDialogsProps, WithTranslation {
  placeId: string
  onClose: () => void
}

interface GooglePlacesDetailsState {
  data: GooglePlacesInfo | null
  photoUrls: string[] | null
}

class GooglePlacesDetails extends React.Component<GooglePlacesDetailsProps, GooglePlacesDetailsState> {
  constructor(props: GooglePlacesDetailsProps) {
    super(props);
    this.state = { data: null, photoUrls: null };
    this.loadDetails();
  }

  async loadDetails() {
    const { t } = this.props;

    const data = await getGooglePlacesInfo(this.props.placeId);

    if (!data) {
      this.props.alertDialog(
        t('Please check your google places ID or try again later...').split('\n'),
        t('Error loading Google Places Information'),
        t('Ok'),
        this.props.onClose,
      );
      return;
    }
    let photoUrls: string[] | null = ['/static/images/photo-placeholder.png'];
    if (data.photos) {
      const photoReferences = data.photos.map((p) => p.photo_reference);
      photoUrls = await getGooglePlacesPhotoUrls(photoReferences, 600);
    }
    this.setState({ data, photoUrls });
  }

  // eslint-disable-next-line react/sort-comp
  render() {
    const { t } = this.props;

    return (
      <Dialog
        onClose={this.props.onClose}
        aria-labelledby="customized-dialog-title"
        open
        fullWidth
        maxWidth="xl"
      >
        <ClosableDialogTitle onClose={this.props.onClose}>
          {t('Google Places Info')}
        </ClosableDialogTitle>
        <ClosableDialogContent dividers>
          {this.renderPlaceInfo()}
        </ClosableDialogContent>
      </Dialog>
    );
  }

  renderPlaceInfo() {
    const { t } = this.props;

    if (!this.state.data) return <LoadingBounce style={{ height: '70vh' }} />;
    const defaultReadOnlyText = '';
    const { data } = this.state;
    const readOnlyTextInputProps: GlobalTextInputProps = {
      variant: 'standard',
      InputProps: { readOnly: true },
      style: { display: 'flex', flexWrap: 'wrap' },
    };

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Grid container spacing={2}>
            <Grid item xs={1} md={1}>
              <img src={data.icon} alt={t('Google Places Icon')} style={{ height: 40, width: 40 }} />
            </Grid>
            <Grid item xs={11} md={7}>
              <TextField
                {...readOnlyTextInputProps}
                label={t('Name')}
                value={data.name || defaultReadOnlyText}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                {...readOnlyTextInputProps}
                label={t('Phone Number')}
                value={data.formatted_phone_number || defaultReadOnlyText}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <TextField
                {...readOnlyTextInputProps}
                label={t('Address')}
                value={data.formatted_address || defaultReadOnlyText}
              />
            </Grid>
            <Grid item xs={4} md={2}>
              <TextField
                {...readOnlyTextInputProps}
                label={t('Price Level')}
                value={data.price_level || defaultReadOnlyText}
              />
            </Grid>
            <Grid item xs={4} md={1}>
              <TextField
                {...readOnlyTextInputProps}
                label={t('Rating')}
                value={data.rating || defaultReadOnlyText}
              />
            </Grid>
            <Grid item xs={4} md={2}>
              <TextField
                {...readOnlyTextInputProps}
                label={t('Ratings count')}
                value={data.user_ratings_total || defaultReadOnlyText}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <TextField
                {...readOnlyTextInputProps}
                label={t('Website')}
                value={data.website || defaultReadOnlyText}
                InputProps={{
                  ...readOnlyTextInputProps.InputProps,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        disabled={!data.website}
                        aria-label={t('Open link in new tab')}
                        onClick={() => window.open(data.website, '_blank')}
                      >
                        <OpenInNew />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <span style={{ color: '#666', fontSize: '0.9em' }}>{t('Categories/Types')}</span>
              <ul style={{ marginTop: 7 }}>
                {(data.types || [' ']).map((s, i) => <li key={`type-${i}`}>{s.replace(/_/g, ' ')}</li>)}
              </ul>
            </Grid>
            <Grid item xs={12} md={6}>
              <span style={{ color: '#666', fontSize: '0.9em' }}>{t('Hours of Operations')}</span>
              <ul style={{ marginTop: 7 }}>
                {data.opening_hours && (data.opening_hours.weekday_text || [' ']).map((s, i) => <li key={`hoo-${i}`}>{s}</li>)}
              </ul>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={5} style={{ padding: 17 }}>
          <Map
            id="googlePlacesMap"
            label={data.name || ''}
            style={{ height: 350 }}
            options={{ zoom: 16 }}
            fixedMarker={data.geometry.location}
            readOnly
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Panel title={t('User Reviews')} variant="default">
            <Grid container spacing={2} style={{ overflow: 'auto', maxHeight: 400, height: 400 }}>
              {this.renderReviews()}
            </Grid>
          </Panel>
        </Grid>
        <Grid item xs={12} md={6}>
          <Panel title={t('Photos')} variant="default">
            <Grid container spacing={2} style={{ overflow: 'auto', maxHeight: 400, height: 400 }}>
              {this.renderPhotos()}
            </Grid>
          </Panel>
        </Grid>
      </Grid>
    );
  }

  renderPhotos() {
    if (!this.state.photoUrls) return <LoadingBounce style={{ height: '20vh' }} />;
    let firstCol = 1;
    switch (this.state.photoUrls.length % 3) {
      case 1: firstCol = 3; break;
      case 2: firstCol = 2; break;
      default: break;
    }
    return (
      <GridList cellHeight={this.state.photoUrls.length === 1 ? 390 : 160} cols={3} style={{ paddingTop: 5 }}>
        {this.state.photoUrls.map((url, i) => (
          <GridListTile key={`photo-${i}`} cols={i === 0 ? firstCol : 1}>
            <img src={url} alt={`Google Places ${i}`} />
          </GridListTile>
        ))}
      </GridList>
    );
  }

  renderReviews() {
    const { t } = this.props;

    if (!this.state.data) return <LoadingBounce style={{ height: '20vh' }} />;
    const { data } = this.state;
    const defaultReadOnlyText = '';
    const readOnlyTextInputProps: GlobalTextInputProps = {
      variant: 'standard',
      InputProps: { readOnly: true },
      style: { display: 'flex', flexWrap: 'wrap' },
    };

    return (
      <Grid item xs={12}>
        {data.reviews?.map((r, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Grid container spacing={2} key={`review-${i}`}>
            <Grid item xs={1}>
              <img src={r.profile_photo_url} alt={r.author_name} style={{ height: 40 }} />
            </Grid>
            <Grid item xs={10}>
              <TextField
                {...readOnlyTextInputProps}
                label={t('Author')}
                value={r.author_name || defaultReadOnlyText}
              />
            </Grid>
            <Grid item xs={1}>
              <TextField
                {...readOnlyTextInputProps}
                label={t('Rating')}
                InputProps={{
                  ...readOnlyTextInputProps.InputProps,
                  style: { textAlign: 'center' },
                }}
                value={r.rating || defaultReadOnlyText}
              />
            </Grid>
            <Grid item xs={12} style={{ marginBottom: 30, paddingTop: 0 }}>
              <TextField
                {...readOnlyTextInputProps}
                multiline
                value={r.text || defaultReadOnlyText}
              />
            </Grid>
          </Grid>
        ))}
      </Grid>
    );
  }
}

const GooglePlacesDetailsWithStuff = withTranslation()(withDialogs(GooglePlacesDetails));
export {
  GooglePlacesDetailsWithStuff as GooglePlacesDetails,
};
